import {getAuthTokenFromLocalStorage} from '@features/auth';
import {Centrifuge, PublicationContext, State, Subscription} from 'centrifuge';

type PublicationHandler = (data: unknown) => void;

interface ChannelSubscription {
  handlers: Map<symbol, PublicationHandler>;
  subscription: Subscription;
}

const channelSubscriptions = new Map<string, ChannelSubscription>();
let centrifugeClient: Centrifuge | null = null;

const reportRealtimeError = (message: string, context?: unknown) => {
  console.error(`[Centrifugo] ${message}`, context);
};

const getCentrifugeUrl = () => {
  const url = import.meta.env.VITE_CENTRIFUGO_URL?.trim();

  if (!url) {
    throw new Error('VITE_CENTRIFUGO_URL is not configured');
  }

  return url;
};

export const getCentrifugeClient = (): Centrifuge => {
  if (centrifugeClient) return centrifugeClient;

  const client = new Centrifuge(getCentrifugeUrl(), {
    token: getAuthTokenFromLocalStorage() ?? '',
  });

  client.on('error', context => {
    reportRealtimeError('connection error', context);
  });
  client.on('disconnected', context => {
    if (channelSubscriptions.size > 0) {
      reportRealtimeError('connection stopped', context);
    }
  });

  centrifugeClient = client;
  return client;
};

const createChannelSubscription = (
  client: Centrifuge,
  channel: string,
): ChannelSubscription => {
  const handlers = new Map<symbol, PublicationHandler>();
  const subscription = client.newSubscription(channel);
  const entry = {handlers, subscription};

  subscription.on('publication', ({data}: PublicationContext) => {
    handlers.forEach(handler => handler(data));
  });
  subscription.on('error', context => {
    reportRealtimeError(`subscription error for "${channel}"`, context);
  });
  subscription.on('unsubscribed', context => {
    if (handlers.size > 0) {
      reportRealtimeError(`subscription stopped for "${channel}"`, context);
    }
  });

  subscription.subscribe();
  return entry;
};

export const subscribeToChannel = (
  channel: string,
  handler: PublicationHandler,
): (() => void) => {
  const client = getCentrifugeClient();
  let entry = channelSubscriptions.get(channel);

  if (!entry) {
    try {
      entry = createChannelSubscription(client, channel);
      channelSubscriptions.set(channel, entry);
    } catch (error) {
      reportRealtimeError(`failed to subscribe to "${channel}"`, error);
      throw error;
    }
  }

  const activeEntry = entry;
  const handlerId = Symbol(channel);
  activeEntry.handlers.set(handlerId, handler);

  if (client.state === State.Disconnected) {
    try {
      client.setToken(getAuthTokenFromLocalStorage() ?? '');
      client.connect();
    } catch (error) {
      activeEntry.handlers.delete(handlerId);

      if (activeEntry.handlers.size === 0) {
        activeEntry.subscription.unsubscribe();
        client.removeSubscription(activeEntry.subscription);
        channelSubscriptions.delete(channel);
      }

      reportRealtimeError('failed to start connection', error);
      throw error;
    }
  }

  let isUnsubscribed = false;

  return () => {
    if (isUnsubscribed) return;

    isUnsubscribed = true;
    activeEntry.handlers.delete(handlerId);

    if (activeEntry.handlers.size > 0) return;

    activeEntry.subscription.unsubscribe();
    client.removeSubscription(activeEntry.subscription);
    channelSubscriptions.delete(channel);

    if (channelSubscriptions.size === 0) {
      client.disconnect();
    }
  };
};
